import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '@/lib/api';
import { AppResponse, UserResponse } from '@troia-v3/types';

/**
 * AppContext - Application-level context
 *
 * Provides global app data and utility functions accessible throughout the application.
 * This context wraps AuthProvider and provides:
 *
 * - Current app data (logo, name, domain) from `/api/apps/me`
 * - Users list for dropdowns from `/api/users/dropdown`
 * - Loading states for both resources
 *
 * Usage:
 * ```tsx
 * const { app, users, isLoadingApp, isLoadingUsers } = useApp();
 * ```
 */

interface AppContextType {
  // App data
  app: AppResponse | null;
  isLoadingApp: boolean;
  appError: string | null;

  // Users data for dropdowns
  users: UserResponse[];
  isLoadingUsers: boolean;
  usersError: string | null;

  // Methods
  refreshApp: () => Promise<void>;
  refreshUsers: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // App state
  const [app, setApp] = useState<AppResponse | null>(null);
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const [appError, setAppError] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  /**
   * Fetch current app data from `/api/apps/me`
   * Public endpoint - NO authentication required (used in login page)
   */
  const fetchApp = async () => {
    try {
      setIsLoadingApp(true);
      setAppError(null);

      const response = await api.get('/apps/me');

      if (response.data.success) {
        setApp(response.data.data);
      } else {
        setAppError('Failed to load app data');
      }
    } catch (error: any) {
      console.error('Error fetching app data:', error);
      setAppError(error.response?.data?.message || 'Failed to load app data');
    } finally {
      setIsLoadingApp(false);
    }
  };

  /**
   * Fetch users for dropdown from `/api/users/dropdown`
   * Authenticated endpoint - requires valid JWT token
   */
  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      setUsersError(null);

      const response = await api.get('/users/dropdown');

      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        setUsersError('Failed to load users');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);

      // Don't set error if user is not authenticated (expected for login page)
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        setUsersError(error.response?.data?.message || 'Failed to load users');
      }
    } finally {
      setIsLoadingUsers(false);
    }
  };

  /**
   * Refresh app data manually
   */
  const refreshApp = async () => {
    await fetchApp();
  };

  /**
   * Refresh users data manually
   */
  const refreshUsers = async () => {
    await fetchUsers();
  };

  // Fetch app data on mount (always - even for unauthenticated users)
  useEffect(() => {
    fetchApp();
  }, []);

  /**
   * Update document metadata (title, favicon, theme-color) when app data changes
   * This enables dynamic white-label branding based on the current app
   */
  useEffect(() => {
    if (app) {
      // Update page title
      document.title = app.name;

      // Update favicon
      let faviconLink = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      faviconLink.href = app.logo;

      // Update Apple touch icon (for iOS bookmarks)
      let appleTouchIcon = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleTouchIcon);
      }
      // iOS exige PNG quadrado — preferir o ícone gerado (192x192, fundo sólido) ao logo cru
      appleTouchIcon.href = app.pwaIcons?.icon192 || app.logo;

      // Update theme-color meta tag (affects browser UI on mobile)
      let metaThemeColor = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.name = 'theme-color';
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.content = app.themeOverrides?.colors?.primary || '#8b5cf6';
    }
  }, [app]);

  /**
   * Fetch users when authentication token is available
   * Listens for storage changes to detect login/logout events
   */
  useEffect(() => {
    const checkAndFetchUsers = () => {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

      // Don't retry if there's already an error or if already loading
      if (token && users.length === 0 && !isLoadingUsers && !usersError) {
        fetchUsers();
      }
    };

    // Initial check
    checkAndFetchUsers();

    // Listen for storage events (login/logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue) {
        // Token was added - fetch users (clear error first)
        setUsersError(null);
        fetchUsers();
      } else if (e.key === 'auth_token' && !e.newValue) {
        // Token was removed - clear users and errors
        setUsers([]);
        setUsersError(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [users.length, isLoadingUsers, usersError]);

  const value: AppContextType = {
    app,
    isLoadingApp,
    appError,
    users,
    isLoadingUsers,
    usersError,
    refreshApp,
    refreshUsers
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

/**
 * Hook to access AppContext
 *
 * @throws Error if used outside AppProvider
 *
 * @example
 * ```tsx
 * const { app, users } = useApp();
 *
 * // Display app logo
 * <img src={app?.logo} alt={app?.name} />
 *
 * // Populate user dropdown
 * <select>
 *   {users.map(user => (
 *     <option key={user.id} value={user.id}>
 *       {user.firstName} {user.lastName}
 *     </option>
 *   ))}
 * </select>
 * ```
 */
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
};
